import {
  Accordion,
  Anchor,
  Button,
  Center,
  Group,
  Loader,
  Modal,
  Paper,
  SimpleGrid,
  Stack,
  Text,
  Title,
  useMantineTheme,
} from "@mantine/core";
import React, { FC, useCallback, useEffect, useState } from "react";
import { AiJobRecommendResultResponse } from "../models/AiJobRecommendResultResponse";
import { fetchWithAuthentication } from "../utilities/FetchUtil";
import { convertToLocalDateTime } from "../utilities/DateUtil";
import { AiSelectedJob } from "../models/AiSelectedJob";
import { AiTaskStatus } from "../models/Message";
import { FaCheckCircle } from "react-icons/fa";

interface HistoricalJobRecommendationsModalProps {
  modalOpened: boolean;
  modalHandlers: {
    readonly open: () => void;
    readonly close: () => void;
    readonly toggle: () => void;
  };
  chatSessionId: string | undefined;
  sentOrLikedIsLoading: boolean;
  markJobAsSentToUser: (job: AiSelectedJob) => Promise<void>;
  markJobAsLikedOrDisliked: (
    job: AiSelectedJob,
    jobRecommendTaskId: string | null,
    userLiked: boolean,
  ) => Promise<void>;
  updateHasJobAlert: (chatSessionId: string, hasJobAlert: boolean) => void;
}

interface HistoricalJobRecommendError {
  code: number;
  message: string;
}

const HistoricalJobRecommendationsModal: FC<
  HistoricalJobRecommendationsModalProps
> = ({
  modalHandlers,
  modalOpened,
  chatSessionId,
  markJobAsSentToUser,
  sentOrLikedIsLoading,
  markJobAsLikedOrDisliked,
  updateHasJobAlert,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [jobRecommendationResultList, setJobRecommendationResultList] =
    useState<AiJobRecommendResultResponse[] | null>(null);
  const [error, setError] = useState<HistoricalJobRecommendError | null>(null);

  const theme = useMantineTheme();

  const getHistoricalJobRecommendations = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    const response = await fetchWithAuthentication(
      `/job_recommend/history/${chatSessionId}`,
      "GET",
      null,
    );
    if (response.ok) {
      const json = await response.json();
      const sortedTasks = json.aiJobRecommendResultResponses.sort(
        (a: AiJobRecommendResultResponse, b: AiJobRecommendResultResponse) => {
          const same = a.enabled === b.enabled;
          const different = a.enabled ? -1 : 1;
          return same ? 0 : different;
        },
      );
      setJobRecommendationResultList(sortedTasks);
    } else {
      setError({ code: response.status, message: response.statusText });
    }
    setIsLoading(false);
  }, [chatSessionId]);

  useEffect(() => {
    if (modalOpened) {
      getHistoricalJobRecommendations();
    }
  }, [modalOpened, getHistoricalJobRecommendations]);

  const toggleJobAlert = useCallback(
    async (jobRecommendTaskId: string, enable: boolean) => {
      const url = enable
        ? "/job_recommend/job_alert/enable"
        : "/job_recommend/job_alert/disable";

      const response = await fetchWithAuthentication(url, "POST", {
        jobRecommendTaskId,
      });

      if (response.ok) {
        console.log(
          `Job alert ${enable ? "enabled" : "disabled"} successfully`,
        );
        await getHistoricalJobRecommendations();
        updateHasJobAlert(chatSessionId!, enable);
      } else {
        console.error(`Failed to ${enable ? "enable" : "disable"} job alert`);
        setError({ code: response.status, message: response.statusText });
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [getHistoricalJobRecommendations],
  );

  const renderAccordionControl = (
    jobRecommendationResult: AiJobRecommendResultResponse,
  ) => {
    const { taskStatus, selectedJobs } = jobRecommendationResult;

    if (
      taskStatus === AiTaskStatus.RUNNING ||
      taskStatus === AiTaskStatus.PENDING
    ) {
      return <Text fw={600}>Task is running...</Text>;
    }

    if (taskStatus === AiTaskStatus.DONE) {
      if (selectedJobs.length === 0) {
        return (
          <Text fw={600}>Task is done but no jobs with score &gt;= 60.</Text>
        );
      }
      return (
        <Text fw={600}>
          Task is done and found {selectedJobs.length} jobs with score &gt;= 60.
          Click to see the jobs.
        </Text>
      );
    }

    if (taskStatus === AiTaskStatus.OOPS) {
      return (
        <Text fw={600}>Task is failed. Click to see the failed reason.</Text>
      );
    }

    return null;
  };

  const renderAccordionPanel = (
    jobRecommendationResult: AiJobRecommendResultResponse,
  ) => {
    if (jobRecommendationResult.taskStatus === AiTaskStatus.OOPS) {
      return (
        <Text fw={600}>
          Failed reason: {jobRecommendationResult.failedReason}
        </Text>
      );
    }

    return (
      <SimpleGrid cols={3}>
        {jobRecommendationResult.selectedJobs.map((job, index) => (
          <Paper key={job.jobKey + index} withBorder p="md">
            <Stack>
              <Title order={3}>{job.jobTitle}</Title>
              <Text fw={600}>{job.location}</Text>
              <Text fw={600}>
                AI score: <Text span>{job.score}</Text>
              </Text>
              <Text fw={600}>
                Reasons: <Text span>{job.reasons}</Text>
              </Text>
              <Text fw={600}>
                Job URL:{" "}
                <Anchor
                  href={`https://www.indeed.com/viewjob?jk=${job.jobKey}`}
                  target="_blank"
                >
                  {`https://www.indeed.com/viewjob?jk=${job.jobKey}`}
                </Anchor>
              </Text>

              {!job.sentToChat && !sentOrLikedIsLoading && (
                <>
                  <Button onClick={() => markJobAsSentToUser(job)}>
                    Send link to chat
                  </Button>
                  <Text fw={300} size="sm">
                    Clicking this button will copy the job into the chat message
                    field. Once you send the message it will become marked as
                    being sent to the user.
                  </Text>
                </>
              )}

              {job.sentToChat &&
                job.userLiked == null &&
                !sentOrLikedIsLoading && (
                  <Group grow>
                    <Button
                      onClick={() =>
                        markJobAsLikedOrDisliked(
                          job,
                          jobRecommendationResult.jobRecommendTaskId,
                          true,
                        )
                      }
                    >
                      User liked
                    </Button>
                    <Button
                      onClick={() =>
                        markJobAsLikedOrDisliked(
                          job,
                          jobRecommendationResult.jobRecommendTaskId,
                          false,
                        )
                      }
                      color={theme.colors.red[5]}
                    >
                      User Disliked
                    </Button>
                  </Group>
                )}

              {job.userLiked !== null && !sentOrLikedIsLoading && (
                <Paper
                  h="34px"
                  bg={
                    job.userLiked ? theme.colors.blue[5] : theme.colors.red[5]
                  }
                >
                  <Center h="100%">
                    <Text c="white">
                      {job.userLiked ? "Liked" : "Disliked"}
                    </Text>
                  </Center>
                </Paper>
              )}
            </Stack>
          </Paper>
        ))}
      </SimpleGrid>
    );
  };

  const shouldShowAccordionPanel = (
    jobRecommendationResult: AiJobRecommendResultResponse,
  ) => {
    return (
      jobRecommendationResult.taskStatus === AiTaskStatus.OOPS ||
      (jobRecommendationResult.taskStatus === AiTaskStatus.DONE &&
        jobRecommendationResult.selectedJobs.length > 0)
    );
  };

  const renderJobRecommendationContent = (
    jobRecommendationResult: AiJobRecommendResultResponse,
  ) => {
    const hasExpandableContent = shouldShowAccordionPanel(
      jobRecommendationResult,
    );

    if (hasExpandableContent) {
      return (
        <Accordion variant="separated">
          <Accordion.Item
            key={jobRecommendationResult.jobRecommendTaskId}
            value={jobRecommendationResult.jobRecommendTaskId}
          >
            <Accordion.Control>
              {renderAccordionControl(jobRecommendationResult)}
            </Accordion.Control>
            <Accordion.Panel>
              {renderAccordionPanel(jobRecommendationResult)}
            </Accordion.Panel>
          </Accordion.Item>
        </Accordion>
      );
    }

    return (
      <Paper withBorder={false} p="xs" bg="gray.0">
        {renderAccordionControl(jobRecommendationResult)}
      </Paper>
    );
  };

  return (
    <Modal
      title="Historical job recommendations"
      opened={modalOpened}
      onClose={modalHandlers.close}
      size="100%"
    >
      <Stack>
        {isLoading && (
          <Center>
            <Loader />
          </Center>
        )}

        {error && (
          <Stack>
            <Title order={4}>{error.code}</Title>
            <Text>{error.message}</Text>
            <Text>Something went wrong, please try again.</Text>
            <Button onClick={() => getHistoricalJobRecommendations()}>
              OK
            </Button>
          </Stack>
        )}

        {!isLoading &&
          !error &&
          jobRecommendationResultList?.map(
            (jobRecommendationResult: AiJobRecommendResultResponse) => (
              <Paper
                key={jobRecommendationResult.jobRecommendTaskId}
                withBorder
                p="md"
              >
                <Stack>
                  <Group justify="space-between">
                    <Title order={4}>
                      {convertToLocalDateTime(
                        jobRecommendationResult.taskCreatedAt,
                      )}
                    </Title>
                    {jobRecommendationResult.enabled && (
                      <Group>
                        <Text>Job alerts enabled</Text>
                        <FaCheckCircle color="green" size={16} />
                        <Button
                          onClick={() =>
                            toggleJobAlert(
                              jobRecommendationResult.jobRecommendTaskId,
                              false,
                            )
                          }
                        >
                          Disable job alerts
                        </Button>
                      </Group>
                    )}
                    {!jobRecommendationResult.enabled &&
                      jobRecommendationResult.taskStatus ===
                        AiTaskStatus.DONE && (
                        <Button
                          disabled={jobRecommendationResultList[0].enabled}
                          onClick={() =>
                            toggleJobAlert(
                              jobRecommendationResult.jobRecommendTaskId,
                              true,
                            )
                          }
                        >
                          Enable job alerts
                        </Button>
                      )}
                  </Group>
                  <Text fw={600}>
                    Job title:{" "}
                    <Text span>{jobRecommendationResult.jobTitle}</Text>
                  </Text>
                  <Text fw={600}>
                    Location:{" "}
                    <Text span>{jobRecommendationResult.location}</Text>
                  </Text>
                  <Text fw={600}>
                    Radius (miles):{" "}
                    <Text span>{jobRecommendationResult.radius}</Text>
                  </Text>
                  <Text fw={600}>
                    Custom requirements:{" "}
                    <Text span style={{ whiteSpace: "pre-wrap" }}>
                      {jobRecommendationResult.customRequirements.length === 0
                        ? "None"
                        : jobRecommendationResult.customRequirements}
                    </Text>
                  </Text>

                  {renderJobRecommendationContent(jobRecommendationResult)}
                </Stack>
              </Paper>
            ),
          )}
      </Stack>
    </Modal>
  );
};

export default HistoricalJobRecommendationsModal;
